import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers,
    faArrowLeft,
    faArrowRight,
    faInfoCircle,
    faCheckCircle,
    faFilter,
    faSort,
    faChartLine
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function AnalyticsClientes() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Estadísticas de Clientes
                    </h1>
                    <p className="text-xl text-gray-600">
                        Análisis detallado del comportamiento y métricas de tus clientes con herramientas de filtrado y segmentación avanzadas.
                    </p>
                </div>

                {/* Acceso */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-blue-600" />
                        ¿Cómo acceder?
                    </h2>
                    <p className="text-gray-700 mb-2">
                        Para acceder a las Estadísticas de Clientes:
                    </p>
                    <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                        <li>Inicia sesión en tu cuenta de PetitPrime</li>
                        <li>En el menú principal, selecciona <strong>"Analytics"</strong></li>
                        <li>Haz clic en <strong>"Estadísticas de Clientes"</strong></li>
                        <li>O accede directamente a: <code className="bg-white px-2 py-1 rounded">/statistics/customer</code></li>
                    </ol>
                </div>

                {/* Vista de Lista */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        1. Vista de Lista de Clientes
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Cuando accedes sin especificar un cliente, verás una lista completa de todos tus clientes con herramientas de filtrado y ordenamiento.
                        </p>

                        {/* Filtros */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon icon={faFilter} className="mr-2 text-blue-600" />
                                Filtros Disponibles
                            </h3>
                            <div className="space-y-4">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 mb-2">Filtro por Segmento</h4>
                                    <p className="text-sm text-gray-700 mb-2">
                                        Permite filtrar clientes según su segmentación RFM:
                                    </p>
                                    <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                        <li><strong>Todos:</strong> Muestra todos los clientes sin filtrar</li>
                                        <li><strong>Champions:</strong> Clientes de alto valor y frecuencia</li>
                                        <li><strong>Loyal Customers:</strong> Clientes leales regulares</li>
                                        <li><strong>At Risk:</strong> Clientes en riesgo de abandono</li>
                                        <li><strong>Can't Lose:</strong> Clientes valiosos que no puedes perder</li>
                                        <li><strong>About to Sleep:</strong> Clientes que están dejando de comprar</li>
                                        <li><strong>Lost:</strong> Clientes perdidos</li>
                                    </ul>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 mb-2">Filtro por Visita</h4>
                                    <p className="text-sm text-gray-700 mb-2">
                                        Filtra según la última visita del cliente:
                                    </p>
                                    <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                        <li><strong>Todos:</strong> Muestra todos los clientes</li>
                                        <li><strong>Recientes:</strong> Clientes que visitaron en los últimos 30 días</li>
                                        <li><strong>Antiguos:</strong> Clientes que no visitan hace más de 30 días</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Ordenamiento */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon icon={faSort} className="mr-2 text-blue-600" />
                                Opciones de Ordenamiento
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Puedes ordenar la lista de clientes por:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li><strong>Segmento:</strong> Agrupa clientes por su segmentación RFM</li>
                                    <li><strong>Última Visita:</strong> Ordena por fecha de la última compra</li>
                                    <li><strong>Nombre:</strong> Ordena alfabéticamente por nombre</li>
                                </ul>
                                <p className="text-sm text-gray-600 mt-2">
                                    Cada opción puede ordenarse de forma ascendente o descendente.
                                </p>
                            </div>
                        </div>

                        {/* Acceso a Detalle */}
                        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                            <p className="text-sm text-gray-700">
                                <strong>💡 Tip:</strong> Haz clic en cualquier cliente de la lista para ver sus estadísticas detalladas individuales.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Vista de Detalle */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        2. Vista de Detalle de Cliente
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Al hacer clic en un cliente específico o acceder directamente con su ID, verás métricas detalladas de ese cliente individual.
                        </p>

                        {/* Métricas Principales */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon icon={faChartLine} className="mr-2 text-green-600" />
                                Métricas Principales
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 mb-2">Total de Puntos</h4>
                                    <p className="text-sm text-gray-600">
                                        Suma total de puntos acumulados por el cliente a lo largo del tiempo.
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 mb-2">Total de Canjes</h4>
                                    <p className="text-sm text-gray-600">
                                        Número total de veces que el cliente ha canjeado puntos por premios.
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 mb-2">Valor Promedio de Transacción</h4>
                                    <p className="text-sm text-gray-600">
                                        Promedio del valor monetario de cada compra realizada por el cliente.
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 mb-2">Frecuencia de Visitas Diaria</h4>
                                    <p className="text-sm text-gray-600">
                                        Promedio de visitas diarias del cliente a tus tiendas.
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 mb-2">Frecuencia de Visitas Semanal</h4>
                                    <p className="text-sm text-gray-600">
                                        Promedio de visitas semanales del cliente.
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 mb-2">Frecuencia de Visitas Mensual</h4>
                                    <p className="text-sm text-gray-600">
                                        Promedio de visitas mensuales del cliente.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Información del Cliente */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Información del Cliente
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    En la parte superior de la página de detalle verás:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Nombre completo del cliente</li>
                                    <li>Dirección de email</li>
                                    <li>Segmentación RFM actual</li>
                                </ul>
                            </div>
                        </div>

                        {/* Historial de Compras */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Historial de Compras
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Si el cliente tiene tickets registrados, podrás ver:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Lista de todas las compras realizadas</li>
                                    <li>Fecha de cada transacción</li>
                                    <li>Monto de cada compra</li>
                                    <li>Puntos acumulados en cada transacción</li>
                                    <li>Premios canjeados en cada compra</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Interpretación de Datos */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        3. Interpretación de los Datos
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Para aprovechar al máximo las Estadísticas de Clientes:
                        </p>
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Segmentación RFM:</strong> Usa los filtros de segmento para identificar grupos de clientes que requieren atención específica. Por ejemplo, los clientes "At Risk" pueden beneficiarse de campañas de recuperación.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Frecuencia de Visitas:</strong> Clientes con alta frecuencia pueden ser candidatos para programas VIP o recompensas especiales.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Valor Promedio:</strong> Identifica clientes de alto valor para ofrecerles productos premium o servicios personalizados.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Historial de Compras:</strong> Analiza patrones de compra para personalizar ofertas y recomendaciones.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Troubleshooting */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        4. Solución de Problemas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No se muestran clientes en la lista</h4>
                                <p className="text-gray-700 text-sm">
                                    Asegúrate de tener clientes registrados en tu marca. Si acabas de crear tu cuenta, es normal que la lista esté vacía hasta que registres tus primeros clientes.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Los filtros no funcionan</h4>
                                <p className="text-gray-700 text-sm">
                                    Verifica que los clientes tengan segmentación RFM asignada. Si no tienen segmentación, el filtro por segmento puede no mostrar resultados.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Las métricas muestran "N/D" o cero</h4>
                                <p className="text-gray-700 text-sm">
                                    Esto indica que el cliente no tiene transacciones registradas aún. Las métricas se calcularán automáticamente cuando el cliente realice su primera compra.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-8 border-t">
                    <Link
                        to="/documentation/analytics/dashboard"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Anterior: Dashboard Analytics
                    </Link>
                    <Link
                        to="/documentation/analytics/tiendas"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                        Siguiente: Estadísticas de Tiendas
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default AnalyticsClientes;

